import * as React from 'react'
import { Box, Chip, List, Typography, Sheet, IconButton, Stack } from '@mui/joy'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import type { TruckLocationHistoryRow } from '../TruckHistory'
import { sortTruckHistoryByTimestampAsc } from '../utils/truckHistoryUtils'
import { formatToUserLocalTime } from '@/utils/date-formatting'

interface SelectedTrucksSidebarProps {
    trucks: TruckLocationHistoryRow[]
    activeVin: string | null
    onActiveVinChange: (vin: string | null) => void
    onStopClick: (row: TruckLocationHistoryRow) => void
}

export const SelectedTrucksSidebar: React.FC<SelectedTrucksSidebarProps> = ({ trucks, activeVin, onActiveVinChange, onStopClick }) => {
    const sortedTrucks = React.useMemo(() => sortTruckHistoryByTimestampAsc(trucks), [trucks])

    const groupedByVin = React.useMemo(() => {
        const map = new Map<
            string,
            {
                vin: string
                name: string
                projectName: string
                points: TruckLocationHistoryRow[]
            }
        >()

        sortedTrucks.forEach((row) => {
            const existing = map.get(row.vin)
            if (existing) {
                existing.points.push(row)
            } else {
                map.set(row.vin, {
                    vin: row.vin,
                    name: row.name || 'Unknown',
                    projectName: row.projectName || '',
                    points: [row],
                })
            }
        })

        return Array.from(map.values())
    }, [sortedTrucks])

    const [expandedVin, setExpandedVin] = React.useState<string | null>(null)

    const handleTruckHeaderClick = (vin: string) => {
        // Select this VIN for the map route if it's not already active
        if (activeVin !== vin) {
            onActiveVinChange(vin)
        }

        // Toggle only the dropdown visibility, not the active route selection
        setExpandedVin((prev) => (prev === vin ? null : vin))
    }

    return (
        <Box
            sx={{
                width: { xs: '100%', sm: '320px' },
                borderRight: { xs: 'none', sm: '1px solid' },
                borderBottom: { xs: '1px solid', sm: 'none' },
                borderColor: 'divider',
                height: { xs: '250px', sm: '100%' },
                bgcolor: 'background.surface',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': { display: 'none' },
            }}
        >
            <Box
                sx={{
                    p: 2,
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.level1',
                }}
            >
                <Typography level="title-sm" fontWeight="md">
                    Selected Trucks
                </Typography>
                <Chip size="sm" variant="soft" color="neutral">
                    {groupedByVin.length}
                </Chip>
            </Box>

            <List
                sx={{
                    '--ListItem-paddingY': '0.5rem',
                    '--ListItem-paddingX': '0.75rem',
                    flex: 1,
                    overflow: 'clip',
                    p: 0,
                }}
            >
                {groupedByVin.map((group) => {
                    const isExpanded = expandedVin === group.vin

                    return (
                        <Sheet
                            key={group.vin}
                            variant="outlined"
                            sx={{
                                borderRadius: 0,
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderTop: 'none',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Truck header row (dropdown toggle) */}
                            <Box
                                sx={{
                                    p: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleTruckHeaderClick(group.vin)}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography level="body-sm" fontWeight="md" noWrap>
                                        {group.name}
                                        {group.projectName ? ` . ${group.projectName}` : ''}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                                        VIN {group.vin}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip size="sm" variant="soft" color="neutral">
                                        {group.points.length} stops
                                    </Chip>
                                    <IconButton size="sm" variant="plain" color="neutral">
                                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                </Stack>
                            </Box>

                            {/* Stops list for this truck */}
                            {isExpanded && (
                                <Box
                                    sx={{
                                        pb: 1,
                                    }}
                                >
                                    {group.points.map((row, index) => (
                                        <Box
                                            key={`${group.vin}-${index}`}
                                            sx={{
                                                px: 1.5,
                                                py: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                            onClick={() => onStopClick(row)}
                                        >
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'primary.100',
                                                    color: 'primary.700',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.875rem',
                                                    mr: 1.5,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {index + 1}
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                                                    {row.timestamp ? formatToUserLocalTime(row.timestamp) : 'N/A'}
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                                                    {row.lat}, {row.lng}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Sheet>
                    )
                })}

                {groupedByVin.length === 0 && (
                    <Box sx={{ p: 2 }}>
                        <Typography level="body-xs">No points selected.</Typography>
                    </Box>
                )}
            </List>
        </Box>
    )
}
